import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchActiveFormDraft,
  FORM_DRAFT_DEFAULT_VERSION,
  FORM_DRAFT_DISCARDED_STATUS,
  FORM_DRAFT_SUBMITTED_STATUS,
  markFormDraftStatus,
  readFormDraftFormValues,
  readFormDraftSavedAt,
  saveFormDraft,
} from './formDrafts';

export const PERSISTENT_FORM_DRAFT_AUTOSAVE_DELAY_MS = 1000;
export const PERSISTENT_FORM_DRAFT_SUPABASE_STORAGE = 'supabase';
export const PERSISTENT_FORM_DRAFT_LOCAL_STORAGE = 'localStorage';

function trimText(value) {
  return String(value ?? '').trim();
}

function serializeDraftPayload(payload) {
  try {
    return JSON.stringify(payload ?? {});
  } catch (_error) {
    return '';
  }
}

function formatDraftTime(value) {
  if (!value) return '';

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return '';

  return parsedDate.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function getLocalStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage || null;
  } catch (_error) {
    return null;
  }
}

function buildLocalStorageDraftKey({ formKey, entityId, formVersion }) {
  const normalizedFormKey = trimText(formKey);
  const normalizedEntityId = trimText(entityId) || '__new__';
  const normalizedFormVersion = trimText(formVersion) || FORM_DRAFT_DEFAULT_VERSION;
  return `outreachos:form-draft:${normalizedFormKey}:${normalizedFormVersion}:${normalizedEntityId}`;
}

function readLocalStorageDraft({ formKey, entityId, formVersion }) {
  const storage = getLocalStorage();
  const storageKey = buildLocalStorageDraftKey({ formKey, entityId, formVersion });
  if (!storage || !trimText(formKey)) return null;

  const rawDraft = storage.getItem(storageKey);
  if (!rawDraft) return null;

  try {
    const parsedDraft = JSON.parse(rawDraft);
    if (!parsedDraft || parsedDraft.status !== 'active') return null;

    if (parsedDraft.expires_at) {
      const expirationDate = new Date(parsedDraft.expires_at);
      if (!Number.isNaN(expirationDate.getTime()) && expirationDate.getTime() <= Date.now()) {
        storage.removeItem(storageKey);
        return null;
      }
    }

    return {
      id: parsedDraft.id || storageKey,
      form_key: formKey,
      entity_id: trimText(entityId) || null,
      form_version: trimText(formVersion) || FORM_DRAFT_DEFAULT_VERSION,
      payload: parsedDraft.payload || {},
      status: parsedDraft.status,
      last_saved_at: parsedDraft.last_saved_at || null,
      expires_at: parsedDraft.expires_at || null,
      created_at: parsedDraft.created_at || null,
      updated_at: parsedDraft.updated_at || null,
      storage_key: storageKey,
    };
  } catch (_error) {
    storage.removeItem(storageKey);
    return null;
  }
}

function saveLocalStorageDraft({ formKey, entityId, formVersion, payload, now = new Date() }) {
  const storage = getLocalStorage();
  const storageKey = buildLocalStorageDraftKey({ formKey, entityId, formVersion });
  if (!storage || !trimText(formKey)) return null;

  const existingDraft = readLocalStorageDraft({ formKey, entityId, formVersion });
  const savedAt = now.toISOString();
  const draftRecord = {
    id: existingDraft?.id || storageKey,
    form_key: trimText(formKey),
    entity_id: trimText(entityId) || null,
    form_version: trimText(formVersion) || FORM_DRAFT_DEFAULT_VERSION,
    payload: payload && typeof payload === 'object' ? payload : {},
    status: 'active',
    last_saved_at: savedAt,
    expires_at: new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000)).toISOString(),
    created_at: existingDraft?.created_at || savedAt,
    updated_at: savedAt,
    storage_key: storageKey,
  };

  storage.setItem(storageKey, JSON.stringify(draftRecord));
  return draftRecord;
}

function clearLocalStorageDraft({ formKey, entityId, formVersion }) {
  const storage = getLocalStorage();
  if (!storage || !trimText(formKey)) return null;

  const storageKey = buildLocalStorageDraftKey({ formKey, entityId, formVersion });
  const existingDraft = readLocalStorageDraft({ formKey, entityId, formVersion });
  storage.removeItem(storageKey);
  return existingDraft;
}

export default function usePersistentFormDraft({
  enabled = true,
  storage = PERSISTENT_FORM_DRAFT_SUPABASE_STORAGE,
  userId,
  formKey,
  entityId = null,
  formVersion = FORM_DRAFT_DEFAULT_VERSION,
  formValues,
  source,
  autosaveEnabled = false,
  autosaveDelayMs = PERSISTENT_FORM_DRAFT_AUTOSAVE_DELAY_MS,
  restoreFormValues,
  buildPayload,
  extraAutosaveDeps = [],
} = {}) {
  const useLocalStorage = storage === PERSISTENT_FORM_DRAFT_LOCAL_STORAGE;
  const canUseStorage = enabled && trimText(formKey) && (useLocalStorage || trimText(userId));
  const [draftReady, setDraftReady] = useState(!canUseStorage);
  const [restoreCandidate, setRestoreCandidate] = useState(null);
  const [draftState, setDraftState] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [hasPendingSave, setHasPendingSave] = useState(false);

  const mountedRef = useRef(true);
  const autosaveTimerRef = useRef(null);
  const draftIdRef = useRef(null);
  const lastSavedPayloadRef = useRef(null);
  const saveInFlightRef = useRef(null);
  const queuedSaveRef = useRef(false);
  const formValuesRef = useRef(formValues);
  const restoreCandidateRef = useRef(restoreCandidate);
  const autosaveEnabledRef = useRef(autosaveEnabled);
  const saveCurrentDraftRef = useRef(async () => null);
  const extraAutosaveSignature = useMemo(
    () => serializeDraftPayload(extraAutosaveDeps),
    [extraAutosaveDeps]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  useEffect(() => {
    restoreCandidateRef.current = restoreCandidate;
  }, [restoreCandidate]);

  useEffect(() => {
    autosaveEnabledRef.current = autosaveEnabled;
  }, [autosaveEnabled]);

  const buildCurrentPayload = useCallback((currentValues) => {
    if (typeof buildPayload === 'function') {
      return buildPayload({
        formValues: currentValues,
        source,
        savedAt: new Date().toISOString(),
      });
    }

    return {
      formValues: currentValues && typeof currentValues === 'object' ? { ...currentValues } : {},
      savedAt: new Date().toISOString(),
      source: trimText(source) || trimText(formKey) || 'form',
    };
  }, [buildPayload, formKey, source]);

  useEffect(() => {
    let active = true;

    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    draftIdRef.current = null;
    lastSavedPayloadRef.current = null;
    queuedSaveRef.current = false;
    setRestoreCandidate(null);
    setLastSavedAt(null);
    setHasPendingSave(false);
    setDraftState('idle');

    if (!canUseStorage) {
      setDraftReady(true);
      return () => {
        active = false;
      };
    }

    setDraftReady(false);

    const loadDraft = async () => {
      try {
        const row = useLocalStorage
          ? readLocalStorageDraft({ formKey, entityId, formVersion })
          : await fetchActiveFormDraft({ formKey, entityId, formVersion });

        if (!active || !mountedRef.current) return;

        draftIdRef.current = row?.id || null;
        lastSavedPayloadRef.current = row?.payload ? serializeDraftPayload(row.payload) : null;
        setLastSavedAt(readFormDraftSavedAt(row));

        const savedFormValues = readFormDraftFormValues(row);
        if (savedFormValues) {
          setRestoreCandidate({
            row,
            formValues: savedFormValues,
            savedAt: readFormDraftSavedAt(row),
          });
        }
      } catch (error) {
        console.error(`Error loading ${formKey} draft:`, error);
      } finally {
        if (active && mountedRef.current) {
          setDraftReady(true);
        }
      }
    };

    void loadDraft();

    return () => {
      active = false;
    };
  }, [canUseStorage, entityId, formKey, formVersion, useLocalStorage]);

  const saveCurrentDraft = async ({ force = false } = {}) => {
    if (!canUseStorage || !draftReady || restoreCandidateRef.current) {
      if (!saveInFlightRef.current) setHasPendingSave(false);
      return null;
    }

    if (!autosaveEnabledRef.current) {
      setHasPendingSave(false);
      return null;
    }

    const payload = buildCurrentPayload(formValuesRef.current);
    const serializedPayload = serializeDraftPayload(payload);

    if (!force && serializedPayload === lastSavedPayloadRef.current) {
      setHasPendingSave(false);
      return null;
    }

    if (saveInFlightRef.current) {
      queuedSaveRef.current = true;
      return saveInFlightRef.current;
    }

    setDraftState('saving');
    setHasPendingSave(true);

    const savePromise = Promise.resolve(
      useLocalStorage
        ? saveLocalStorageDraft({ formKey, entityId, formVersion, payload })
        : saveFormDraft({
          draftId: draftIdRef.current,
          userId,
          formKey,
          entityId,
          formVersion,
          payload,
        })
    )
      .then((row) => {
        if (!mountedRef.current) return row;

        draftIdRef.current = row?.id || draftIdRef.current;
        lastSavedPayloadRef.current = serializedPayload;
        setLastSavedAt(readFormDraftSavedAt(row) || payload.savedAt);
        setDraftState('saved');
        return row;
      })
      .catch((error) => {
        console.error(`Error saving ${formKey} draft:`, error);
        if (mountedRef.current) {
          setDraftState('error');
        }
        return null;
      })
      .finally(async () => {
        saveInFlightRef.current = null;

        if (queuedSaveRef.current) {
          queuedSaveRef.current = false;
          await saveCurrentDraftRef.current({ force });
          return;
        }

        if (mountedRef.current) {
          setHasPendingSave(false);
        }
      });

    saveInFlightRef.current = savePromise;
    return savePromise;
  };

  saveCurrentDraftRef.current = saveCurrentDraft;

  useEffect(() => {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    if (!canUseStorage || !draftReady || restoreCandidate || !autosaveEnabled) {
      return undefined;
    }

    const payload = buildCurrentPayload(formValues);
    const serializedPayload = serializeDraftPayload(payload);

    if (serializedPayload === lastSavedPayloadRef.current) {
      setHasPendingSave(false);
      return undefined;
    }

    setHasPendingSave(true);
    autosaveTimerRef.current = window.setTimeout(() => {
      void saveCurrentDraftRef.current();
    }, autosaveDelayMs);

    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [
    autosaveDelayMs,
    autosaveEnabled,
    buildCurrentPayload,
    canUseStorage,
    draftReady,
    extraAutosaveSignature,
    formValues,
    restoreCandidate,
  ]);

  useEffect(() => {
    if (!canUseStorage || !draftReady) return undefined;

    const flushDraft = () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }

      void saveCurrentDraftRef.current({ force: true });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushDraft();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', flushDraft);
    window.addEventListener('beforeunload', flushDraft);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', flushDraft);
      window.removeEventListener('beforeunload', flushDraft);
    };
  }, [canUseStorage, draftReady]);

  const continueDraft = async () => {
    if (!restoreCandidateRef.current) return null;

    const nextRestoreCandidate = restoreCandidateRef.current;
    const nextFormValues = typeof restoreFormValues === 'function'
      ? restoreFormValues(nextRestoreCandidate.formValues)
      : nextRestoreCandidate.formValues;

    draftIdRef.current = nextRestoreCandidate.row?.id || draftIdRef.current;
    lastSavedPayloadRef.current = nextRestoreCandidate.row?.payload
      ? serializeDraftPayload(nextRestoreCandidate.row.payload)
      : lastSavedPayloadRef.current;
    setLastSavedAt(nextRestoreCandidate.savedAt);
    setDraftState(lastSavedPayloadRef.current ? 'saved' : 'idle');
    setHasPendingSave(false);
    setRestoreCandidate(null);

    return {
      formValues: nextFormValues,
      savedAt: nextRestoreCandidate.savedAt,
    };
  };

  const discardDraft = async () => {
    const draftToDiscard = restoreCandidateRef.current;
    setRestoreCandidate(null);
    setHasPendingSave(false);

    if (!draftToDiscard?.row?.id) {
      draftIdRef.current = null;
      lastSavedPayloadRef.current = null;
      setLastSavedAt(null);
      setDraftState('idle');
      return true;
    }

    try {
      if (useLocalStorage) {
        clearLocalStorageDraft({ formKey, entityId, formVersion });
      } else {
        await markFormDraftStatus({
          draftId: draftToDiscard.row.id,
          status: FORM_DRAFT_DISCARDED_STATUS,
        });
      }
      draftIdRef.current = null;
      lastSavedPayloadRef.current = null;
      setLastSavedAt(null);
      setDraftState('idle');
      return true;
    } catch (error) {
      console.error(`Error discarding ${formKey} draft:`, error);
      setDraftState('error');
      return false;
    }
  };

  const clearDraft = async ({ status = FORM_DRAFT_SUBMITTED_STATUS } = {}) => {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    setHasPendingSave(false);
    setRestoreCandidate(null);

    if (saveInFlightRef.current) {
      try {
        await saveInFlightRef.current;
      } catch (_error) {
        // Final submit is authoritative even if the draft save failed.
      }
    }

    const activeDraftId = draftIdRef.current || restoreCandidateRef.current?.row?.id;
    if (!activeDraftId) {
      lastSavedPayloadRef.current = null;
      setLastSavedAt(null);
      setDraftState('idle');
      return true;
    }

    try {
      if (useLocalStorage) {
        clearLocalStorageDraft({ formKey, entityId, formVersion });
      } else {
        await markFormDraftStatus({
          draftId: activeDraftId,
          status,
        });
      }
      draftIdRef.current = null;
      lastSavedPayloadRef.current = null;
      setLastSavedAt(null);
      setDraftState('idle');
      return true;
    } catch (error) {
      console.error(`Error clearing ${formKey} draft:`, error);
      setDraftState('error');
      return false;
    }
  };

  const draftStatusText = useMemo(() => {
    if (draftState === 'error') return 'Draft save failed';
    if (hasPendingSave || draftState === 'saving') return 'Saving draft...';
    if (lastSavedAt) {
      const formattedTime = formatDraftTime(lastSavedAt);
      return formattedTime ? `Draft saved ${formattedTime}` : 'Draft saved';
    }
    return '';
  }, [draftState, hasPendingSave, lastSavedAt]);

  return {
    draftReady,
    restoreCandidate,
    continueDraft,
    discardDraft,
    clearDraft,
    draftStatusText,
    draftStatusTone: draftState === 'error' ? 'error' : 'default',
    restoreSavedAtLabel: formatDraftTime(restoreCandidate?.savedAt),
  };
}
